import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchDocuments, 
  createDocument, 
  updateDocument, 
  deleteDocument,
  clearError,
  clearSuccess
} from '../../data/documentSlice';

const Documents = () => {
  const dispatch = useDispatch();
  const { items: documents, loading, error, success } = useSelector(state => state.documents);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    document_type: 'pdf',
    file: null
  });
  const [previewFile, setPreviewFile] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  // Load documents on component mount
  useEffect(() => {
    dispatch(fetchDocuments());
  }, [dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      setSuccessMessage(currentDocument ? 'Document updated successfully!' : 'Document uploaded successfully!');
      setTimeout(() => {
        setSuccessMessage('');
        dispatch(clearSuccess());
      }, 3000);
    }
  }, [success, dispatch, currentDocument]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setTimeout(() => {
        setErrorMessage('');
        dispatch(clearError());
      }, 5000);
    }
  }, [error, dispatch]);

  // Handle opening the document form modal
  const handleOpenModal = (document = null) => {
    if (document) {
      setCurrentDocument(document);
      setFormData({
        title: document.title || '',
        document_type: document.document_type || 'pdf',
        file: null
      });
      setPreviewFileName(document.title || '');
      setPreviewFile('');
    } else {
      setCurrentDocument(null);
      setFormData({
        title: '',
        document_type: 'pdf',
        file: null
      });
      setPreviewFile('');
      setPreviewFileName('');
    }
    setErrorMessage('');
    setIsModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        file: file
      });
      setPreviewFileName(file.name);
      
      // Create preview URL for the selected file
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewFile(reader.result);
      };
      
      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file);
      } else {
        setPreviewFile('');
      }
    }
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.document_type) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      if (currentDocument) {
        // Update existing document
        await dispatch(updateDocument({ 
          documentId: currentDocument.id, 
          documentData: formData 
        })).unwrap();
      } else {
        // Create new document - file is required for new documents
        if (!formData.file) {
          setErrorMessage('File is required for new documents');
          setIsLoading(false);
          return;
        }
        await dispatch(createDocument(formData)).unwrap();
      }

      // Close modal and reset form
      setIsModalOpen(false);
      setFormData({ title: '', document_type: 'pdf', file: null });
      setPreviewFile('');
      setPreviewFileName('');
      setCurrentDocument(null);
    } catch (err) {
      setErrorMessage(err || 'An error occurred while saving the document');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = (document) => {
    setCurrentDocument(document);
    setIsDeleteModalOpen(true);
  };

  // Confirm document deletion
  const confirmDelete = async () => {
    if (currentDocument) {
      setIsLoading(true);
      try {
        await dispatch(deleteDocument(currentDocument.id)).unwrap();
        setIsDeleteModalOpen(false);
        setCurrentDocument(null);
      } catch (err) {
        setErrorMessage('Failed to delete document');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Get file icon based on type
  const getFileIcon = (doc) => {
    if (doc.document_type === 'pdf' || doc.file_type?.includes('pdf')) {
      return (
        <svg className="w-12 h-12 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z"/>
        </svg>
      );
    }
    return (
      <svg className="w-12 h-12 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.3A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z"/>
      </svg>
    );
  };

  // Get file preview or icon
  const getFilePreview = (doc) => {
    if (doc.fileData && doc.document_type === 'image') {
      return (
        <img 
          src={doc.fileData} 
          alt={doc.title}
          className="w-full h-full object-cover"
        />
      );
    }
    return getFileIcon(doc);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center transition"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Upload Document
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex justify-between items-center">
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage('')} className="text-green-700 font-bold">✕</button>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex justify-between items-center">
          <p>{errorMessage}</p>
          <button onClick={() => setErrorMessage('')} className="text-red-700 font-bold">✕</button>
        </div>
      )}

      {/* Loading indicator */}
      {loading && !isLoading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Documents Grid */}
      {!loading && documents && documents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map(document => 
            document ? (
              <div key={document.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition flex flex-col">
                {/* Document Preview */}
                <div className="h-48 bg-gray-100 overflow-hidden flex items-center justify-center">
                  {getFilePreview(document)}
                </div>
                
                {/* Document Info */}
                <div className="p-4 flex-grow flex flex-col">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{document.title || 'Untitled'}</h3>
                  <div className="mb-3 flex-grow">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      document.document_type === 'pdf' 
                        ? 'bg-red-100 text-red-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {document.document_type?.toUpperCase() || 'FILE'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Created: {new Date(document.createdAt).toLocaleDateString()}
                  </p>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    {document.fileData && (
                      <a
                        href={document.fileData}
                        download={`${document.title}.${document.document_type === 'pdf' ? 'pdf' : 'jpg'}`}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2 px-3 rounded transition text-center"
                      >
                        Download
                      </a>
                    )}
                    <button
                      onClick={() => handleOpenModal(document)}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-2 px-3 rounded transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(document)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold py-2 px-3 rounded transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && (!documents || documents.length === 0) && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3.75A2.75 2.75 0 011 18.25V5.75A2.75 2.75 0 013.75 3h16.5A2.75 2.75 0 0123 5.75v12.5A2.75 2.75 0 0120.25 21h-5" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No documents found</h3>
          <p className="mt-1 text-gray-500">Upload your first document to get started</p>
        </div>
      )}

      {/* Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {currentDocument ? 'Edit Document' : 'Upload Document'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Document Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter document title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Document Type Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Document Type *
                </label>
                <select
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="pdf">PDF</option>
                  <option value="image">Image</option>
                </select>
              </div>

              {/* File Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Document File {!currentDocument && '*'}
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept={formData.document_type === 'pdf' ? '.pdf' : 'image/*'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!currentDocument}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.document_type === 'pdf' 
                    ? 'Upload a PDF file (max 10MB)'
                    : 'Upload an image file (max 5MB)'}
                </p>
              </div>

              {/* File Preview */}
              {previewFile && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Preview:</p>
                  <img
                    src={previewFile}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* File Name Display */}
              {previewFileName && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">File:</span> {previewFileName}
                  </p>
                </div>
              )}

              {/* Modal Footer */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition disabled:opacity-50"
                >
                  {isLoading ? 'Uploading...' : currentDocument ? 'Update' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-lg font-bold text-gray-900">Confirm Delete</h2>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>{currentDocument?.title}</strong>? This action cannot be undone.
              </p>

              {/* Modal Footer */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition disabled:opacity-50"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
